<template>
  <div class="inputs">
    <div class="form-group names" v-show="!noUserInfo">
      <input
        v-model="value.firstName"
        id="first_name"
        type="text"
        class="text-input dark"
        placeholder="First name"
        :hidden="noUserInfo"
      />
      <input
        v-model="value.lastName"
        id="last_name"
        type="text"
        class="text-input dark"
        placeholder="Last name"
        :hidden="noUserInfo"
      />
    </div>
    <div class="form-group" v-show="!noUserInfo">
      <input
        v-model="value.email"
        id="email"
        type="email"
        class="text-input dark"
        placeholder="Email address"
        :hidden="noUserInfo"
      />
    </div>
    <div class="form-group">
      <input
        v-model="value.talentName"
        type="text"
        class="text-input dark"
        placeholder="Artist/Producer Name*"
      />
      <div
        v-if="
          value.talentName &&
            (spotifySearchResults.length > 0 || spotifySearchLoading)
        "
        class="search-results"
      >
        <div @scroll="handleSpotifySearchScroll($event)" class="wrapper">
          <div
            class="result"
            :class="{ selected: artist.id === value.spotify_id }"
            v-for="artist in spotifySearchResults"
            :key="artist.id"
            @click="selectSpotifyArtist(artist)"
          >
            <div class="avatar">
              <img :src="artist.image" />
            </div>
            <span class="name">{{ artist.name }}</span>
          </div>
          <template v-if="spotifySearchLoading">
            <div class="flex items-center justify-start w-full px-5 py-1">
              <div
                class="w-8 h-8 mr-2 bg-gray-200 rounded-full animate-pulse"
              ></div>
              <div
                style="max-width: 250px;"
                class="flex-1 h-4 bg-gray-200 rounded animate-pulse"
              ></div>
            </div>
          </template>
        </div>
      </div>
    </div>
    <div class="form-group">
      <AddressInput v-model="value.address" title="Address*" />
    </div>
    <div class="form-group" v-show="!notSigned">
      <CustomSelect
        :options="[
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' }
        ]"
        @setValue="setRecordingAgreement"
        class="rep-selector dark left"
        :val="value.recording_agreement"
        default="Has recording agreement?"
        noMargin
      />
    </div>
    <div class="form-group" v-show="!notSigned">
      <CustomSelect
        :options="[
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' }
        ]"
        @setValue="setRecordingAgreementThirdParty"
        class="rep-selector dark left"
        :val="value.recording_agreement_third_party"
        default="Has recording agreement with 3rd party?"
        noMargin
      />
    </div>
    <div class="form-group">
      <input
        v-model="value.PROAffiliation"
        type="text"
        class="text-input dark"
        placeholder="PRO Affiliation*"
      />
    </div>
    <div class="form-group">
      <input
        v-model="value.songwriterIPINo"
        type="text"
        class="text-input dark"
        placeholder="Songwriter IPI Number*"
      />
    </div>
    <div class="form-group">
      <input
        v-model="value.publishingAdmin"
        type="text"
        class="text-input dark"
        placeholder="Publisher"
      />
    </div>
    <div class="form-group">
      <input
        v-model="value.publisherIPINo"
        type="text"
        class="text-input dark"
        placeholder="Publisher IPI Number"
      />
    </div>
  </div>
</template>

<script>
import AddressInput from "@/components/AddressInput.vue";
import CustomSelect from "@/components/Select.vue";
import { debounce } from "lodash";
import gql from "graphql-tag";

const SPOTIFY_SEARCH_ARTISTS = gql`
  query spotify_search_artists($limit: Int!, $offset: Int!, $query: String!) {
    spotify_search_artists(limit: $limit, offset: $offset, query: $query) {
      id
      image
      name
      url
    }
  }
`;

export default {
  name: "AddArtistForm",
  components: { AddressInput, CustomSelect },
  props: {
    value: {
      type: Object,
      required: true
    },
    noUserInfo: {
      type: Boolean,
      default: false
    },
    notSigned: {
      type: Boolean,
      default: false
    },
    companyName: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      repOptions: [
        { value: "label", label: "Label" },
        { value: "loan_out_company", label: "Loan-out Company" }
      ],
      spotifySearchOffset: 0,
      spotifySearchLimit: 10,
      spotifySearchResults: [],
      spotifySearchLoading: false,
      preventSpotifySearch: false
    };
  },
  computed: {
    signedToOptions() {
      return [
        { value: "none", label: "No" },
        { value: "to_company", label: `Yes (to ${this.companyName})` },
        { value: "to_third_party", label: "Yes (to third-party label)" }
      ];
    }
  },
  watch: {
    "value.talentName"(newName, oldName) {
      if (this.preventSpotifySearch) {
        this.preventSpotifySearch = false;
        return;
      }
      this.spotifySearchOffset = 0;
      this.spotifySearchResults = [];
      if (newName && newName !== oldName) {
        this.debouncedSearchSpotify(newName);
      }
    }
  },
  created() {
    this.debouncedSearchSpotify = debounce(this.searchSpotify, 500);
  },
  methods: {
    setRecordingAgreement(val) {
      this.value.recording_agreement = val;
    },
    setRecordingAgreementThirdParty(val) {
      this.value.recording_agreement_third_party = val;
    },
    selectSpotifyArtist(artist) {
      this.value.spotify_id = artist.id;
      this.value.talentName = artist.name;
      this.value.avatar_url = artist.image;
      this.spotifySearchResults = [];
      this.spotifySearchOffset = 0;
      this.spotifySearchLoading = false;
      this.preventSpotifySearch = true;
    },
    async handleSpotifySearchScroll(event) {
      const { offsetHeight, scrollTop, scrollHeight } = event.target;
      if (offsetHeight + scrollTop >= scrollHeight) {
        this.spotifySearchOffset = this.spotifySearchOffset + 10;
        await this.searchSpotify(this.value.talentName);
      }
    },
    async searchSpotify(query) {
      if (!query || query.trim().length < 2) return;

      this.spotifySearchLoading = true;
      try {
        const result = await this.$apollo.query({
          query: SPOTIFY_SEARCH_ARTISTS,
          variables: {
            limit: this.spotifySearchLimit,
            offset: this.spotifySearchOffset,
            query: query
          }
        });
        const { spotify_search_artists } = result.data;
        const newResults = spotify_search_artists.filter(
          artist =>
            !this.spotifySearchResults.some(
              existingArtist => existingArtist.id === artist.id
            )
        );
        this.spotifySearchResults = [
          ...this.spotifySearchResults,
          ...newResults
        ];
      } catch (error) {
        console.error("Error searching Spotify artists:", error);
      } finally {
        this.spotifySearchLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/_mixins.scss";

.inputs {
  min-width: 20rem;
  margin-bottom: 32px;
  .form-group {
    margin-bottom: 18px;
    position: relative;
    input {
      @include details-light();
      color: $white;
    }
    span {
      font-weight: 500;
      font-size: 14px;
      line-height: 17px;
      letter-spacing: -0.22px;
      color: #fcfffc;
    }
  }
  .names {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    input {
      width: 48%;
    }
  }
}

.search-results {
  position: absolute;
  top: 100%;
  width: 100%;
  max-height: 180px;
  background: #1e1e1e;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  padding: 8px 8px 8px 0;
  z-index: 10;
  margin-top: 5px;

  .wrapper {
    overflow-y: scroll;
    width: 100%;
    scrollbar-width: thin;
    scrollbar-color: #adadad rgba(#adadad, 0.3);

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      @include background-opacity(#adadad, 0.3);
      border-radius: 5px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #adadad;
      border-radius: 5px;
    }

    .result {
      display: flex;
      align-items: center;
      padding: 8px 10px;
      cursor: pointer;

      &:hover,
      &.selected {
        background-color: #2a2a2a;
      }

      .avatar {
        width: 30px;
        height: 30px;
        aspect-ratio: 1;

        img {
          border-radius: 50%;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      .name {
        color: $white;
        font-weight: 500;
        margin: 0 8px;
      }
    }
  }
}
</style>
